import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import IntegrationIcon from '../integrations/shared/IntegrationIcon';
import { useTranslation } from 'react-i18next';

const BASE_CANVAS_WIDTH = 700;
const BASE_CANVAS_HEIGHT = 600;
const BASE_RADIUS = 180;
const BASE_ICON_OFFSET = 40;
const BASE_HUB_OFFSET = 50;

// Types
interface Integration {
  id: string;
  name: string;
  icon: string;
  status: 'ready' | 'coming' | 'development';
  landingPath?: string;
  isNew?: boolean;
}

interface CentralMemoryHubProps {
  integrations?: Integration[];
  maxDisplayed?: number;
  onIntegrationClick?: (integration: Integration) => void;
  className?: string;
  enableAnimations?: boolean;
}

interface HubLayout {
  canvasWidth: number;
  canvasHeight: number;
  centerX: number;
  centerY: number;
  radius: number;
  iconOffset: number;
  hubOffset: number;
  scale: number;
}

function computeLayout(containerWidth: number): HubLayout {
  const scale = Math.min(containerWidth / BASE_CANVAS_WIDTH, 1);

  return {
    canvasWidth: BASE_CANVAS_WIDTH * scale,
    canvasHeight: BASE_CANVAS_HEIGHT * scale,
    centerX: (BASE_CANVAS_WIDTH * scale) / 2,
    centerY: (BASE_CANVAS_HEIGHT * scale) / 2,
    radius: BASE_RADIUS * scale,
    iconOffset: BASE_ICON_OFFSET * scale,
    hubOffset: BASE_HUB_OFFSET * scale,
    scale,
  };
}

// Particle Component - Optimized for performance
// direction: 'toCenter' = storing memories, 'fromCenter' = recalling memories
const Particle: React.FC<{
  path: string;
  color: string;
  delay: number;
  direction?: 'toCenter' | 'fromCenter';
  sizeScale?: number;
}> = memo(({ path, color, delay, direction = 'toCenter', sizeScale = 1 }) => {
  const size = (direction === 'fromCenter' ? 5 : 6) * sizeScale;
  const duration = direction === 'fromCenter' ? 2.0 : 2.5;

  return (
    <motion.div
      initial={{
        offsetDistance: direction === 'fromCenter' ? '100%' : '0%',
        opacity: 0,
      }}
      animate={{
        offsetDistance:
          direction === 'fromCenter' ? ['100%', '0%'] : ['0%', '100%'],
        opacity: [0, 0.8, 0.8, 0],
      }}
      transition={{
        duration,
        ease: direction === 'fromCenter' ? 'easeOut' : 'linear',
        repeat: Infinity,
        delay,
        times: [0, 0.2, 0.8, 1],
        repeatDelay: 0,
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        background:
          direction === 'fromCenter'
            ? 'linear-gradient(135deg, #10B981 0%, #8B5CF6 100%)'
            : `linear-gradient(135deg, ${color} 0%, #6366F1 100%)`,
        boxShadow:
          direction === 'fromCenter'
            ? '0 0 8px #10B981'
            : `0 0 6px ${color}`,
        offsetPath: `path('${path}')`,
        willChange: 'offset-distance, opacity',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.path === nextProps.path &&
    prevProps.color === nextProps.color &&
    prevProps.delay === nextProps.delay &&
    prevProps.direction === nextProps.direction &&
    prevProps.sizeScale === nextProps.sizeScale
  );
});

Particle.displayName = 'Particle';

const CentralMemoryHub: React.FC<CentralMemoryHubProps> = memo(({
  integrations = [],
  maxDisplayed = 10,
  onIntegrationClick,
  className = '',
  enableAnimations = true,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<HubLayout>(() =>
    computeLayout(BASE_CANVAS_WIDTH)
  );

  const updateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const availableWidth = container.clientWidth || BASE_CANVAS_WIDTH;
    setLayout(computeLayout(availableWidth));
  }, []);

  useEffect(() => {
    updateLayout();

    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateLayout);
      return () => window.removeEventListener('resize', updateLayout);
    }

    const observer = new ResizeObserver(() => {
      updateLayout();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [updateLayout]);

  const hubIntegrations = useMemo(
    () =>
      integrations
        .filter((integration) => integration.status === 'ready')
        .slice(0, maxDisplayed),
    [integrations, maxDisplayed]
  );

  const { canvasWidth, canvasHeight, centerX, centerY, radius, iconOffset, hubOffset, scale } =
    layout;

  const makeCurveToCenter = useCallback(
    (x1: number, y1: number) => {
      const cx = centerX;
      const cy = centerY;
      const dx = (cx - x1) * 0.55;
      const dy = (cy - y1) * 0.55;
      const c1x = x1 + dx * 0.35;
      const c1y = y1 + dy * 0.35;
      const c2x = x1 + dx * 0.85;
      const c2y = y1 + dy * 0.85;
      return `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${cx} ${cy}`;
    },
    [centerX, centerY]
  );

  const positions = useMemo(() => {
    if (hubIntegrations.length === 0) {
      return [];
    }

    const angleStep = (2 * Math.PI) / hubIntegrations.length;
    return hubIntegrations.map((_, index) => {
      const angle = index * angleStep - Math.PI / 2;
      return {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      };
    });
  }, [hubIntegrations.length, centerX, centerY, radius]);

  const paths = useMemo(
    () =>
      positions.map((pos, index) => ({
        key: hubIntegrations[index].id,
        d: makeCurveToCenter(pos.x, pos.y),
        color: '#8B5CF6',
      })),
    [positions, hubIntegrations, makeCurveToCenter]
  );

  const PARTICLES_TO_CENTER = 3;
  const PARTICLES_FROM_CENTER = 2;
  const iconBoxSize = Math.max(80 * scale, 44);
  const iconInnerSize = Math.max(32 * scale, 24);
  const hubLogoSize = Math.max(96 * scale, 64);
  const hubLogoInner = Math.max(56 * scale, 36);

  return (
    <div
      ref={containerRef}
      className={`relative flex w-full items-center justify-center overflow-hidden ${className}`}
      style={{
        height: canvasHeight,
        maxWidth: BASE_CANVAS_WIDTH,
        margin: '0 auto',
      }}
      aria-label={t('hero.memoryCenter', { ns: 'homepage' })}
    >
      <div
        className="relative"
        style={{
          width: canvasWidth,
          height: canvasHeight,
        }}
      >
        <svg
          width={canvasWidth}
          height={canvasHeight}
          className="pointer-events-none absolute inset-0"
          style={{
            transform: 'translateZ(0)',
            willChange: 'auto',
          }}
          aria-hidden
        >
          {paths.map((p, index) => (
            <motion.path
              key={p.key}
              d={p.d}
              stroke={p.color}
              strokeOpacity={0.2}
              strokeWidth={Math.max(2 * scale, 1)}
              fill="none"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.2 }}
              transition={{
                duration: 1,
                delay: 0.3 + index * 0.1,
                ease: 'easeOut',
              }}
            />
          ))}

          <defs>
            <radialGradient id="particle-gradient">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#6366F1" />
            </radialGradient>
          </defs>
        </svg>

        <div
          className="pointer-events-none absolute inset-0"
          style={{
            contain: 'layout style paint',
            willChange: 'auto',
          }}
        >
          {enableAnimations &&
            paths.map((p, pathIndex) => (
              <React.Fragment key={`particles-${p.key}`}>
                {Array.from({ length: PARTICLES_TO_CENTER }).map((_, i) => (
                  <Particle
                    key={`${p.key}-to-${i}`}
                    path={p.d}
                    color={p.color}
                    delay={pathIndex * 0.15 + i * 0.8}
                    direction="toCenter"
                    sizeScale={scale}
                  />
                ))}
                {Array.from({ length: PARTICLES_FROM_CENTER }).map((_, i) => (
                  <Particle
                    key={`${p.key}-from-${i}`}
                    path={p.d}
                    color="#10B981"
                    delay={pathIndex * 0.15 + i * 1.0 + 0.5}
                    direction="fromCenter"
                    sizeScale={scale}
                  />
                ))}
              </React.Fragment>
            ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="absolute z-20 flex flex-col items-center space-y-3"
          style={{
            left: centerX - hubOffset,
            top: centerY - hubOffset,
          }}
        >
          <div
            className="relative flex items-center justify-center rounded-2xl bg-gradient-to-br from-sanctuary to-primary shadow-2xl ring-4 ring-white/20"
            style={{
              width: hubLogoSize,
              height: hubLogoSize,
            }}
          >
            <img
              src="/logos/memara-logo-light.svg"
              alt="Memara"
              style={{ width: hubLogoInner, height: hubLogoInner }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <div className="hidden font-mono text-3xl font-bold text-white">M</div>
          </div>

          <div className="rounded-lg bg-white/90 px-4 py-2 text-center shadow-md backdrop-blur-sm">
            <div className="text-sm font-semibold text-foreground">
              {t('hero.centralHub', { ns: 'homepage' }) || 'Memara'}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('hero.memoryCenter', { ns: 'homepage' }) || 'Memory Center'}
            </div>
          </div>
        </motion.div>

        {hubIntegrations.map((integration, index) => {
          const pos = positions[index];
          if (!pos) return null;

          return (
            <motion.button
              key={integration.id}
              type="button"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.3,
                ease: 'easeOut',
                delay: 0.1 * index,
              }}
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.95 }}
              className="group absolute z-10 cursor-pointer border-0 bg-transparent p-0"
              style={{
                left: pos.x - iconOffset,
                top: pos.y - iconOffset,
                width: iconBoxSize,
                height: iconBoxSize,
                willChange: 'transform',
              }}
              onClick={() => onIntegrationClick?.(integration)}
              title={integration.name}
              aria-label={integration.name}
            >
              <div
                className="relative flex h-full w-full items-center justify-center rounded-2xl border border-border/50 bg-white p-4 shadow-lg transition-all duration-200 hover:shadow-xl group-hover:border-sanctuary/30"
                style={{ padding: Math.max(12 * scale, 8) }}
              >
                <div className="relative flex items-center justify-center">
                  <IntegrationIcon
                    icon={integration.icon}
                    className="h-8 w-8"
                    size={iconInnerSize}
                  />

                  {integration.isNew && (
                    <div className="absolute -right-2 -top-2 rounded-full bg-sanctuary px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                      NEW
                    </div>
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}

        {integrations.length > maxDisplayed && (
          <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
            <div className="rounded-full bg-white/90 px-4 py-2 shadow-md backdrop-blur-sm">
              <div className="text-sm font-medium text-muted-foreground">
                +{integrations.length - maxDisplayed}{' '}
                {t('hero.moreIntegrations', { ns: 'homepage' }) ||
                  'more integrations'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

CentralMemoryHub.displayName = 'CentralMemoryHub';

export default CentralMemoryHub;
export type { CentralMemoryHubProps, Integration };
